import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { each, isEmpty, isNull, isUndefined, toString } from 'lodash';
import uuid from 'react-uuid';
import { ConvertibleNoteModel, DividendRateLedgerItemModel, LiqPrefLedgerItem, SecurityModel } from 'api';
import { captableAction } from 'common/actions';
import { SYSTEM_COLLAPSE, SYSTEM_EXPAND } from 'common/actions/row-groups/types';
import {
  ASSIGNED_TYPE_FOR_NOTES,
  CAP_TABLE_LOCKED_MESSAGE,
  CURRENT_OWNERSHIP_ROW_NUMBER,
  FULLY_DILUTED_OWNERSHIP_ROW_NUMBER,
  INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER,
  LIQUIDATION_PREFERENCE_LEDGER_ALIAS,
  PREFERRED_TERMS_ALIAS,
  SECURITY_TYPE_ROW_NUMBER,
  SHARES_FULLY_DILUTED_ROW_NUMBER,
  SHARES_OUTSTANDING_ROW_NUMBER,
  STRIKE_PRICE_ALIAS,
  TWO_DECIMAL_PLACES,
} from 'common/constants/cap-table';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { CAP_TABLE_REFERENCE_TYPE } from 'common/constants/documents';
import { DEBOUNCE_DELAY } from 'common/constants/general';
import { CAPTABLE_VERSION_FILTER } from 'common/constants/pageFilters';
import { CONVERTIBLE_NOTES, OPTION, PREFERRED_STOCK, UNISSUED_OPTIONS, WARRANT } from 'common/constants/securityTypes';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { useStore } from 'common/store';
import { CapTableInfo } from 'common/types/capTable';
import { UseWorkbookValues } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, EmptyTableMessage } from 'components';
import { FinalAllocation } from 'components/AllocationFinalMessage/AllocationFinalMessage.props';
import { ExtendedFabButton } from 'components/Buttons';
import { ConfirmationCannotDelete, ConfirmationDeleteWithList, ConfirmationDialog } from 'components/Dialogs';
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import { PdfExportTarget } from 'components/PdfExportConfigDialog/hooks/types';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import CapTableContext, { CapTableContextType } from 'context/CapTableContext';
import DialogContext from 'context/DialogContext';
import { useLayoutContext } from 'context/LayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { ICell, IScalarSpreadsheetConfig } from 'models/interfaces/spreadsheet-types/core-types';
import { ConfirmationSaveFinal, ProformaCaptableDialog } from 'pages/CapTable/cap-table/components/index';
import { blankPreferred, securityTemplate } from 'pages/CapTable/cap-table/data';
import { clearUnderlyingSecurity, linkedCellsUpdates, reverseParser } from 'pages/CapTable/cap-table/utilities';
import getOptionName from 'pages/CapTable/cap-table/utilities/getOptionName';
import { useKeyEventForFabButton, useReadFieldAttributes } from 'services/hooks';
import { useUpdateCapTableAndSecuritiesInfo } from 'services/hooks/captable';
import {
  CAPTABLE_MODEL_NAME,
  CAPTABLE_PAGE,
  CONVERTIBLE_NOTE_MODEL_NAME,
  DIV_RATE_LEDGER_ITEM_MODEL_NAME,
  SECURITY_MODEL_NAME,
} from 'services/hooks/useReadFieldAttributes/constants';
import { getBooleanValue } from 'utilities';
import { getLastColumn } from 'utilities/alphabet-utilities';
import { CapTableCell, CaptableColumn, CaptableTableProps, SourceNote } from './CaptableTable.props';
import useCaptableData from '../../utilities/useCaptableData';
import { OptionsLedgerCell } from '../options-ledger/types';

function calcCurrentOwnPercent(allColumns: CaptableColumn[]) {
  const columns = allColumns.filter(({ is_deleted }) => !is_deleted);
  const totalDiluted = columns.reduce((total, { shares_diluted, security_type }) => {
    if (![UNISSUED_OPTIONS, CONVERTIBLE_NOTES].includes(security_type)) {
      return total + parseFloat(shares_diluted);
    }
    return total;
  }, 0);

  function hasRelatedSecurity(security_group_ref: string) {
    return (
      allColumns.flatMap(c => c.convertible_notes).filter(c => c.security_group_ref === security_group_ref).length === 1
    );
  }

  return allColumns.map(column => {
    if (column.is_deleted) {
      return column;
    }
    const { security_type, shares_diluted, security_group_ref } = column;
    let current_own_percent;
    if (security_type === UNISSUED_OPTIONS) {
      current_own_percent = 0;
    } else {
      if (security_type === CONVERTIBLE_NOTES) {
        // sometimes loans turn up NaN's as the current ownership.
        if (security_group_ref && hasRelatedSecurity(security_group_ref)) {
          current_own_percent = 0;
        } else {
          // eslint-disable-next-line no-param-reassign
          column.convertible_notes = column.convertible_notes?.map(note => {
            const current_ownership
              = totalDiluted && note.expected_shares ? parseFloat(note.expected_shares) / totalDiluted : 0;
            return {
              ...note,
              current_ownership: current_ownership.toFixed(10),
            };
          });
        }
      }
      if (isUndefined(current_own_percent)) {
        current_own_percent = totalDiluted ? parseFloat(shares_diluted) / totalDiluted : 0;
      }
    }
    return {
      ...column,
      current_own_percent: current_own_percent.toFixed(10),
    };
  });
}

const initialState = {
  strike_price: SYSTEM_COLLAPSE,
  prefered_terms: SYSTEM_COLLAPSE,
};

const CapTableTable = ({
  measurementDate,
  selectedMeasurementDate,
  saveAsNewProforma,
  filesToSave,
  addReferenceForExistingDocuments,
  notes,
  saveNotes,
  saveSpreadsheetNotes,
  spreadsheetNotesHasChanged,
  spreadsheetNotes,
  notesHasChanged,
  setIsDisplayingRowNumbers,
  setDocumentDisabledStatus,
  onEditCaptable,
}: CaptableTableProps) => {
  const [{ captableInfo, isShowLoadingProgress, companyInfo }] = useStore() as unknown as UseStoreValues;
  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: `${DEFAULT_CURRENCY} ${DEFAULT_CURRENCY_SYMBOL}`,
  });
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [captableId, setCaptableId] = useState<number>();
  const [finalAllocations, setFinalAllocations] = useState<FinalAllocation[]>([]);
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>();
  const [openProformaDialog, setOpenProformaDialog] = useState(false);
  const [persistentDeletedColumns, setPersistentDeletedColumns] = useState<CaptableColumn[]>([]);
  const { showDialog } = useContext(DialogContext);
  const [, updateCapTableData] = useUpdateCapTableAndSecuritiesInfo() as [
    data: CapTableInfo,
    updateCapTableData: (
      id: number,
      columns: CaptableColumn[],
      updatedTableData: CapTableInfo,
      updateGlobalStore: () => void
    ) => Promise<void>
  ];
  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, initialState);
  const [isTableReady, setIsTableReady] = useState(false);
  const fieldAttributes = useReadFieldAttributes(CAPTABLE_PAGE);
  const securityFieldAttrs = fieldAttributes.get(SECURITY_MODEL_NAME);
  const capTableFieldAttrs = fieldAttributes.get(CAPTABLE_MODEL_NAME);
  const convertibleNotesFieldAttrs = fieldAttributes.get(CONVERTIBLE_NOTE_MODEL_NAME);
  const divRateLedgerItemAttrs = fieldAttributes.get(DIV_RATE_LEDGER_ITEM_MODEL_NAME);
  const [hasVariableLiquidationPreferenceLedger, setHasVariableLiquidationPreferenceLedger] = useState(
    !!captableInfo?.has_liquidation_preference_ledger
  );
  const [hasVariableDividendRate, setHasVariableDividendRate] = useState(
    getBooleanValue(captableInfo?.has_dividend_rate_ledger)
  );

  useEffect(() => {
    setHasVariableLiquidationPreferenceLedger(!!captableInfo?.has_liquidation_preference_ledger);
    setHasVariableDividendRate(getBooleanValue(captableInfo?.has_dividend_rate_ledger));
  }, [captableInfo]);

  const existingHiddenNoteColumnsMapRef = useRef<Record<number, number> | null>(null);

  const { updatePageActions, pageFilters, viewOnlyUser, openPdfDialog } = useLayoutContext();

  const savesAsProformaConfirmation = () => {
    setOpenProformaDialog(true);
  };

  const initialFormValues = useMemo(
    () => ({
      isValid: true,
      name: `${captableInfo?.name} Proforma`,
      values: {
        name: '',
      },
      errors: {},
    }),
    [captableInfo]
  );
  const [formState, setFormState] = useState(initialFormValues);
  const [showEmptyTableMessage, setShowEmptyTableMessage] = useState(false);

  const {
    spreadsheet: captableSheet,
    tableTerms,
    visibleColumns,
    setVisibleColumns,
    virtualColumns,
    setColumns,
    columns,
    isButtonDisabled,
    setIsButtonDisabled,
    ...restCaptableProps
  } = useCaptableData({
    captableInfo: captableInfo as CapTableInfo,
    measurementDate,
    fieldAttributes: securityFieldAttrs,
    isDisabled,
    hasVariableLiquidationPreference: hasVariableLiquidationPreferenceLedger,
    hasVariableDividendRate,
  });

  useEffect(() => {
    if (!isEmpty(captableInfo) && pageFilters?.length) {
      const capTableFilterId = CAPTABLE_VERSION_FILTER;
      const { selectedValue } = pageFilters.find(filter => toString(filter.id) === toString(capTableFilterId)) || {};

      setIsButtonDisabled(`${selectedValue}` === '');
    }
  }, [captableInfo, pageFilters, setIsButtonDisabled]);

  const spreadsheets = useMemo(() => [captableSheet], [captableSheet]) as unknown as IScalarSpreadsheetConfig[];

  const {
    onChange: onCellsChanged,
    cells,
    data,
    evaluateSpreadsheets,
    workbook,
    areCellsValid,
    workbookContextValue,
  } = useWorkbook(spreadsheets) as unknown as UseWorkbookValues;

  const { cleanAction } = useContext(UnsavedChangesContext);

  const [updatedColumns, setUpdatedColumns] = useState<CaptableColumn[]>([]);

  const onChange = async (cell: CapTableCell, expr: string, customFn: () => void) => {
    const reversedColumns = onCellsChanged(cell as ICell, expr, customFn)[0] as CaptableColumn[];

    if (visibleColumns?.length && cells) {
      setUpdatedColumns([...reversedColumns]);
    }
    if (cell.alias === LIQUIDATION_PREFERENCE_LEDGER_ALIAS) {
      // this will call parser and update the expressions for the accrued dividends and initial liquidation preference cells
      await evaluateSpreadsheets(spreadsheets);
    }
  };

  // Visible columns based on the last spreadsheet changes
  useEffect(() => {
    // function that compares the order of each captableSheet.columns with the visibleColumns and returns false
    // if the order is different.
    const compareColumns = (columns: CaptableColumn[], visibleColumns: CaptableColumn[]) => {
      if (columns?.length !== visibleColumns?.length) {
        return false;
      }

      for (let i = 0; i < columns?.length; i++) {
        if (columns[i].columnRef !== visibleColumns[i].columnRef) {
          return false;
        }
      }
      return true;
    };

    if (visibleColumns?.length && captableSheet?.cells && compareColumns(captableSheet?.columns, visibleColumns)) {
      const reversedColumns = reverseParser({
        cells: captableSheet.cells,
        columns: visibleColumns,
        fieldAttributes: securityFieldAttrs,
        allowEmptyValues: false,
      });

      // Add deep comparison to prevent unnecessary updates
      const hasChanged = JSON.stringify(reversedColumns) !== JSON.stringify(updatedColumns);
      if (hasChanged) {
        setUpdatedColumns([...reversedColumns]);
      }
    } else if (updatedColumns.length > 0) {
      // Only clear if there's actually something to clear
      setUpdatedColumns([]);
    }
  }, [captableSheet, cells, securityFieldAttrs, visibleColumns, updatedColumns]);

  // Debounced effect to show EmptyTableMessage only after a delay
  useEffect(() => {
    const shouldShowEmptyMessage = isEmpty(visibleColumns) && captableSheet?.tableData?.id;

    if (shouldShowEmptyMessage) {
      const timeout = setTimeout(() => {
        setShowEmptyTableMessage(true);
      }, DEBOUNCE_DELAY);

      return () => clearTimeout(timeout);
    }

    setShowEmptyTableMessage(false);
    return undefined;
  }, [visibleColumns, captableSheet?.tableData?.id]);

  const deletedColumns = useMemo(() => {
    if (columns?.length && cells?.captable) {
      const auxColumns = columns.filter(column => column.is_deleted && column.id !== 0);
      return auxColumns;
    }
    return [];
  }, [columns, cells]);

  // here are visible columns with ledger.
  const columnsWithLedgers = useMemo(() => {
    if (updatedColumns?.length) {
      return updatedColumns.filter(uc => uc.options_ledger?.length > 1);
    }
    return [];
  }, [updatedColumns]);

  // columns with notes
  const columnsWithNotes = useMemo(() => {
    if (updatedColumns?.length) {
      return updatedColumns.filter(column => !isEmpty(column.convertible_notes));
    }
    return [];
  }, [updatedColumns]);

  const getTotalValue = useCallback(
    rowNumber => {
      const totalColumnLegend = getLastColumn(1);
      const totalCell = cells?.captable?.[`TOTALS_${totalColumnLegend + rowNumber}`];
      if (totalCell?.value) {
        const decimalPlaces = capTableFieldAttrs
          ? capTableFieldAttrs[totalCell.alias]?.decimal_places
          : TWO_DECIMAL_PLACES;
        return Number(totalCell.value).toFixed(decimalPlaces);
      }
      return '0';
    },
    [capTableFieldAttrs, cells]
  );

  const calculateDilutedOwnPercent = useCallback(
    rowShares => (rowShares / Number(getTotalValue(SHARES_FULLY_DILUTED_ROW_NUMBER))).toFixed(15),
    [getTotalValue]
  );

  // The current ownership depends on the diluted shares with the exception of unissued options
  const calculateCurrentOwnPercent = useCallback(
    rowShares =>
      (
        rowShares
        / Number(
          visibleColumns?.reduce((acc, column) => {
            if (column.security_type !== UNISSUED_OPTIONS) {
              return acc + Number(column.shares_diluted);
            }
            return acc;
          }, 0)
        )
      ).toFixed(15),
    [visibleColumns]
  );

  const getExistingHiddenNoteColumns = (securities: SecurityModel[]) =>
    securities?.filter(s => s.is_hidden && ![WARRANT, OPTION].includes(+s.security_type!)) || [];

  const getExistingConvertibleNotes = (convertibleNotesFromColumns: CaptableColumn[]) =>
    convertibleNotesFromColumns
      .map(column => column.convertible_notes)
      .flat()
      .filter(note => note.id !== 0);

  const createHiddenSecurity = (
    column: SecurityModel,
    sourceNote: SourceNote,
    assignedSecurityType: number,
    baseContent: SecurityModel,
    baseName: string,
    baseSeniority: number,
    baseLiquidationPreference: number
  ) => ({
    ...baseContent,
    name: `${baseName} - ${sourceNote.note_name}`,
    is_deleted: sourceNote.is_deleted,
    conversion_rate: '1.00',
    investment_date: sourceNote.issuance_date,
    issue_price: sourceNote.conversion_price_per_share || 0,
    security_type: assignedSecurityType,
    current_own_percent: sourceNote.current_ownership || 0,
    security_group_ref: column.security_ref,
    shares_outstanding: sourceNote.expected_shares || 0,
    shares_diluted: sourceNote.expected_shares || 0,
    loan_value: sourceNote.loan_value,
    seniority: baseSeniority,
    liquidation_preference: baseLiquidationPreference,
  });

  const existingConvertibleNotes = useMemo(() => getExistingConvertibleNotes(columnsWithNotes), [columnsWithNotes]);

  const existingHiddenNoteColumns = useMemo(() => getExistingHiddenNoteColumns(captableInfo?.securities ?? []), [captableInfo]);

  // When CapTable Version is changed, reset the hidden note columns map
  useEffect(() => {
    existingHiddenNoteColumnsMapRef.current = null;
  }, [captableId]);

  const existingHiddenNoteColumnsMap = useMemo((): Record<number, number> => {
    if (
      existingHiddenNoteColumnsMapRef.current === null
      && !isEmpty(existingConvertibleNotes)
      && !isEmpty(existingHiddenNoteColumns)
    ) {
      const hiddenNoteColumnsMap = existingHiddenNoteColumns.reduce((map, column) => {
        const noteName = column.name.split(' - ')[1];
        const foundKey = existingConvertibleNotes.find(note => note.note_name === noteName)?.id;
        if (foundKey) {
          // 'Conv Note - Investor Name' -> 'Investor Name'
          const tmpMap: any = { ...map };
          tmpMap[foundKey] = column.id;
          return tmpMap;
        }
        return map;
      }, {});
      existingHiddenNoteColumnsMapRef.current = hiddenNoteColumnsMap;
    }
    return existingHiddenNoteColumnsMapRef.current as Record<number, number>;
  }, [existingConvertibleNotes, existingHiddenNoteColumns]);

  const updatedHiddenNoteColumns = useMemo(() => {
    const hiddenSecurities: CaptableColumn[] = [];
    if (visibleColumns?.length && !isNull(captableInfo) && !isEmpty(existingHiddenNoteColumnsMap)) {
      if (existingConvertibleNotes?.length) {
        existingHiddenNoteColumns.forEach(column => {
          const tmpSourceNote
            = existingConvertibleNotes.find(
              note =>
                note.security_group_ref === column.security_ref || existingHiddenNoteColumnsMap[note.id!] === column.id
            ) || {};

          const sourceNote: Partial<SourceNote> = tmpSourceNote ? { ...tmpSourceNote } : {};
          const sourceColumn
            = columnsWithNotes.find(col => col.id === sourceNote.convertible_note_security) || ({} as CaptableColumn);
          const assignedSecurityType = ASSIGNED_TYPE_FOR_NOTES[sourceNote.model_as_equity!];
          const baseContent
            = assignedSecurityType === PREFERRED_STOCK
              ? column
              : ({ ...column, ...blankPreferred } as unknown as SecurityModel);
          const baseName = sourceColumn.name;
          const baseSeniority = assignedSecurityType === PREFERRED_STOCK ? sourceColumn.seniority : 0;
          const baseLiquidationPreference
            = assignedSecurityType === PREFERRED_STOCK ? sourceColumn.liquidation_preference : 0;

          const security = createHiddenSecurity(
            column,
            sourceNote as SourceNote,
            assignedSecurityType,
            baseContent,
            baseName,
            +baseSeniority!,
            +baseLiquidationPreference!
          );
          hiddenSecurities.push(security as unknown as CaptableColumn);
        });
      }
    }
    return hiddenSecurities;
  }, [
    captableInfo,
    columnsWithNotes,
    visibleColumns,
    existingConvertibleNotes,
    existingHiddenNoteColumns,
    existingHiddenNoteColumnsMap,
  ]);

  const updatedHiddenColumns = useMemo(() => {
    const hiddenSecurities: CaptableColumn[] = [];
    if (visibleColumns?.length) {
      const existingHiddenColumns
        = captableInfo?.securities?.filter(s => s.is_hidden && [WARRANT, OPTION].includes(s.security_type!)) || [];

      const virtualOptionLedgersRows
        = columnsWithLedgers
          .filter(col => isNull(col.id))
          .map(col => col.options_ledger)
          .flat() || [];

      existingHiddenColumns.forEach(col => {
        const ledgerRowData = virtualOptionLedgersRows.find(row => row.security_ref === col.security_ref) || {};
        const { shares, price } = ledgerRowData;
        const { security_group_ref, strike_price } = col;
        const parentColumn = columnsWithLedgers.find(c => c.security_ref === col.security_group_ref)!;
        const name = getOptionName(price, parentColumn?.name ?? '');
        const security = {
          ...col,
          current_own_percent: Number(calculateCurrentOwnPercent(shares)),
          diluted_own_percent: Number(calculateDilutedOwnPercent(shares)),
          name,
          shares_outstanding: shares,
          strike_price,
          // A hidden column not in virtualOptionLedgerRows means it was deleted from the ledger
          is_deleted: isEmpty(ledgerRowData),
          order: visibleColumns.find(vc => vc.security_group_ref === security_group_ref)?.order,
        };
        hiddenSecurities.push(security as unknown as CaptableColumn);
      });
    }
    return hiddenSecurities;
  }, [visibleColumns, captableInfo, columnsWithLedgers, calculateCurrentOwnPercent, calculateDilutedOwnPercent]);

  // memo to create new column from mutated
  const fromVirtualToReal = useMemo(() => {
    if (updatedColumns?.length) {
      const tmpCols = updatedColumns
        .filter(col => isNull(col.id) && ![WARRANT, OPTION].includes(col.security_type))
        .map(col => ({
          ...col,
          id: 0,
          security_group_ref: null,
          underlying_security_ref: null,
          options_ledger: [],
        }));
      return tmpCols;
    }
    return [];
  }, [updatedColumns]);

  useEffect(() => {
    if (updatedColumns) {
      updatedColumns.forEach((updatedCol, index, arr) => {
        const { id, options_ledger } = updatedCol;
        if (!id && options_ledger.length === 1) {
          // Persist the virtual column as an actual security...
          // eslint-disable-next-line no-param-reassign
          arr[index].id = 0;
          // eslint-disable-next-line no-param-reassign
          arr[index].security_group_ref = null;
          // ...and make the security_ref of the ledger row null.
          // This will generate a new hidden security based on that one row.
          // eslint-disable-next-line no-param-reassign
          arr[index].options_ledger = [
            {
              ...options_ledger[0],
              security_ref: null,
            },
          ];
        }
      });
    }
  }, [updatedColumns]);

  // see if I can catch the ones I want
  const deletedBySecurityTypeChange = useMemo(() => {
    if (fromVirtualToReal?.length) {
      const aux = [...fromVirtualToReal];
      let deleted: CaptableColumn[] = [];
      aux.forEach(virtual => {
        const toDelete = columns
          ?.filter(col => col.security_group_ref === virtual.security_ref)
          .map(col => ({ ...col, is_deleted: true }));
        deleted = [...deleted, ...toDelete];
      });
      return deleted;
    }
    return [];
  }, [columns, fromVirtualToReal]);

  const getColumnIndex = (cols: CaptableColumn[], securityRef: string) => {
    const columnByRef = cols.find(column => column.security_ref === securityRef);
    return cols.indexOf(columnByRef as CaptableColumn);
  };

  const hiddenColumns = useMemo(() => {
    const securities: CaptableColumn[] = [];
    // columnsWithLedgers comes from a memo looking at every col with options_ledger.length > 1
    columnsWithLedgers.forEach(column => {
      const { options_ledger: optionsLedger } = column;
      if (optionsLedger.length > 1) {
        // Rows without security refs represent new hidden columns
        optionsLedger
          .filter((ledgerRow: OptionsLedgerCell) => !ledgerRow.security_ref)
          .forEach((row: OptionsLedgerCell) => {
            const name = getOptionName(row.price, column.name);
            securities.push({
              ...column,
              // An options/warrant security with only one row in the ledger does not make up any virtual columns.
              // However, when more rows are added, all of them need to be converted into hidden securities.
              id: column.isVirtualColumn || optionsLedger.length >= 2 ? 0 : null,
              security_ref: uuid(),
              name,
              is_hidden: true,
              security_group_ref: column.security_ref,
              shares_diluted: row.shares.toString(),
              diluted_own_percent: Number(calculateDilutedOwnPercent(row.shares)),
              shares_outstanding: row.shares.toString(),
              current_own_percent: Number(calculateCurrentOwnPercent(row.shares)),
              strike_price: row.price.toString(),
              order: getColumnIndex(updatedColumns, column.security_ref!),
            });
          });
      }
    });

    return securities;
  }, [columnsWithLedgers, calculateDilutedOwnPercent, calculateCurrentOwnPercent, updatedColumns]);

  // hidden columns that come from notes in the CN ledger
  const fromNotesToSecurities = useMemo(() => {
    const securities: CaptableColumn[] = [];
    columnsWithNotes.forEach(column => {
      const { convertible_notes: convertibleNotes } = column;
      if (convertibleNotes.length >= 1) {
        column.convertible_notes
          .filter(ledgerNote => ledgerNote.id === 0 && !ledgerNote.is_deleted)
          .forEach(note => {
            const assignedSecurityType = ASSIGNED_TYPE_FOR_NOTES[note.model_as_equity!];
            const baseContent = assignedSecurityType === PREFERRED_STOCK ? column : { ...column, ...blankPreferred };
            const security = {
              ...baseContent,
              id: 0,
              security_ref: note.security_group_ref,
              name: `${column.name} - ${note.note_name}`,
              is_hidden: true,
              investment_date: note.issuance_date,
              issue_price: note.conversion_price_per_share || 0,
              security_type: assignedSecurityType,
              security_group_ref: column.security_ref,
              shares_outstanding: note.expected_shares || 0,
              shares_diluted: note.expected_shares || 0,
              cap_table: captableInfo?.id,
              convertible_notes: [],
              diluted_own_percent: Number(calculateDilutedOwnPercent(note.expected_shares)),
              current_own_percent: Number(calculateCurrentOwnPercent(note.expected_shares)),
              total_note_value_at_md: note.value_at_md,
              total_note_preference: note.note_principle_amount,
            } as unknown as CaptableColumn;
            securities.push(security);
          });
      }
    });
    return securities;
  }, [columnsWithNotes, captableInfo, calculateDilutedOwnPercent, calculateCurrentOwnPercent]);

  const saveConfirmation = useCallback(
    callback => {
      if (isAllocationFinal) {
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: 'Confirmation',
          },
          content: ConfirmationSaveFinal,
          actions: [
            {
              label: 'Cancel',
              variant: 'text',
              type: 'primary',
            },
            {
              label: 'Save',
              variant: 'contained',
              type: 'success',
              callback,
            },
          ],
        });
      }
    },
    [isAllocationFinal, showDialog]
  );

  const updatedTableData = useMemo(() => {
    if (isEmpty(cells)) {
      return { ...captableInfo };
    }

    let tmpSecurities = !isEmpty(captableInfo?.securities) ? captableInfo?.securities : updatedColumns;
    tmpSecurities = tmpSecurities ?? [];

    // Remove the variable config from the securities if the captable does not variable props configured
    tmpSecurities = tmpSecurities.map((security: SecurityModel | CaptableColumn) => ({
      ...security,
      ...(!hasVariableLiquidationPreferenceLedger && { liquidation_preference_ledger_config: null }),
      ...(!hasVariableDividendRate && { dividend_rate_ledger_items: [] }),
    })) as SecurityModel[];

    return {
      ...captableInfo,
      has_liquidation_preference_ledger: hasVariableLiquidationPreferenceLedger,
      has_dividend_rate_ledger: hasVariableDividendRate,
      securities: tmpSecurities?.filter(s => !s.is_hidden),
      shares_outstanding: getTotalValue(SHARES_OUTSTANDING_ROW_NUMBER),
      shares_diluted: getTotalValue(SHARES_FULLY_DILUTED_ROW_NUMBER),
      current_own_percent: Number(getTotalValue(CURRENT_OWNERSHIP_ROW_NUMBER)).toFixed(15),
      diluted_own_percent: Number(getTotalValue(FULLY_DILUTED_OWNERSHIP_ROW_NUMBER)).toFixed(15),
      initial_liquidation_preference: getTotalValue(INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER),
    };
  }, [
    cells,
    captableInfo,
    updatedColumns,
    getTotalValue,
    hasVariableLiquidationPreferenceLedger,
    hasVariableDividendRate,
  ]);

  const generateNewSecurities = (count: number) => {
    const securityArray: SecurityModel[] = [];

    for (let i = 0; i < count; i++) {
      const newSecurity = {
        ...securityTemplate,
        security_ref: uuid(),
        cap_table: captableInfo!.id!,
      };
      securityArray.push(newSecurity as unknown as SecurityModel);
    }

    return securityArray;
  };

  const addSecurity = (newColumns = 1) => {
    const securities = generateNewSecurities(newColumns);

    const tmpPersistedColumns = reverseParser({
      cells: cells.captable as Record<string, CapTableCell>,
      columns: visibleColumns,
      fieldAttributes: securityFieldAttrs,
    });
    const allColumns = [...securities, ...tmpPersistedColumns, ...deletedColumns];
    setColumns(allColumns as CaptableColumn[]);
  };

  const deleteColumn = useCallback(
    (columnIndex, captableCells) => {
      const tmpPersistedColumns = [
        ...reverseParser({
          cells: captableCells,
          columns: visibleColumns,
          fieldAttributes: securityFieldAttrs,
        }),
      ];

      tmpPersistedColumns[columnIndex].is_deleted = true;
      const relatedDeleted: CaptableColumn[] = [];
      // check if has notes, if it has, delete the related hidden columns
      if (tmpPersistedColumns[columnIndex].security_type === 6) {
        const relatedHiddenSecuritiesRef = tmpPersistedColumns[columnIndex].convertible_notes.map(
          (note: ConvertibleNoteModel) => note.security_group_ref
        );

        captableInfo?.securities?.forEach(security => {
          if (relatedHiddenSecuritiesRef.includes(security.security_ref)) {
            relatedDeleted.push({ ...security, is_deleted: true } as unknown as CaptableColumn);
          }
        });
      }

      setPersistentDeletedColumns([...persistentDeletedColumns, ...relatedDeleted, tmpPersistedColumns[columnIndex]]);
      setColumns([...tmpPersistedColumns, ...deletedColumns]);
    },
    [captableInfo, deletedColumns, persistentDeletedColumns, securityFieldAttrs, setColumns, visibleColumns]
  );

  const sharedProps = {
    workbook,
    onChange,
    page: 'captable',
    setIsDisplayingRowNumbers,
  };

  const cloneColumn = useCallback(
    columnIndex => {
      const tmpPersistedColumns = [
        ...reverseParser({
          cells: captableSheet?.cells as Record<string, CapTableCell>,
          fieldAttributes: securityFieldAttrs,
          columns: visibleColumns,
        }),
      ];

      const column = tmpPersistedColumns[columnIndex];
      if (column) {
        const nameCount = tmpPersistedColumns.filter(s => s.name.toString().includes(`${column.name} copy`)).length + 1;

        const clonedOptions = column.options_ledger?.map((securityRow: OptionsLedgerCell) => ({
          ...securityRow,
          security_ref: undefined,
        }));

        const clonedNotes = column.convertible_notes?.map((note: ConvertibleNoteModel) => ({
          ...note,
          id: 0,
          convertible_note_security: undefined,
          security_group_ref: uuid(),
        }));

        const clonedDivRateLedgerItems = column.dividend_rate_ledger_items.map((item: DividendRateLedgerItemModel) => ({
          ...item,
          id: null,
          security: undefined,
        }));
        // ids for this needs to be undefined as the serializer works that way
        const clonedLiquidationPreferenceConfig = column.liquidation_preference_ledger_config
          ? {
            id: undefined,
            threshold_type: column.liquidation_preference_ledger_config.threshold_type,
            items: column?.liquidation_preference_ledger_config?.items?.map((item: LiqPrefLedgerItem) => ({
              ...item,
              id: undefined,
              ledger_config: undefined,
            })),
          }
          : null;

        const newSecurity = {
          ...column,
          id: 0,
          security_ref: uuid(),
          options_ledger: clonedOptions,
          convertible_notes: clonedNotes,
          dividend_rate_ledger_items: clonedDivRateLedgerItems,
          liquidation_preference_ledger_config: clonedLiquidationPreferenceConfig,
          is_deleted: false,
          name: `${column.name} copy ${nameCount > 1 ? nameCount : ''}`,
        };

        const allColumns = [newSecurity, ...tmpPersistedColumns, ...deletedColumns];
        setColumns(allColumns as CaptableColumn[]);
      }
    },
    [captableSheet, deletedColumns, securityFieldAttrs, setColumns, visibleColumns]
  );

  const showDeleteConfirmation = useCallback(
    columnIndex => {
      const messages: string[] = [];

      const showDialogIfSecurityIsUsed = () => {
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: 'Action not allowed',
          },
          content: ConfirmationCannotDelete,
          contentProps: { messages, itemName: 'security' },
          actions: [
            {
              label: 'Cancel',
              variant: 'contained',
              type: 'primary',
            },
          ],
        });
      };

      const showRegularDeleteDialog = (securityToDelete: CaptableColumn) => {
        const hasMultipleInvestments = securityToDelete.has_multiple_investments;
        const containMultipleInvestments = securityToDelete.multiple_investments?.length;

        if (hasMultipleInvestments && containMultipleInvestments) {
          messages.push('All tranches in Multiple Investments Dates.');
        }
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: `Are you sure you want to permanently delete ${securityToDelete.name}?`,
          },
          content: ConfirmationDeleteWithList,
          contentProps: { messages },
          actions: [
            {
              label: 'Delete',
              variant: 'contained',
              type: 'danger',
              callback: () => deleteColumn(columnIndex, captableSheet?.cells),
            },
          ],
        });
      };

      if (visibleColumns) {
        const security = visibleColumns.find(column => column.order === columnIndex)!;
        const {
          backsolve_usage_details: backsolveUsageDetails,
          fod_usage_details: fodUsageDetails,
        } = security;
        const {
          is_used_in_backsolve: isUsedInBacksolve,
          location: backsolveLocation,
        } = backsolveUsageDetails || {};
        const {
          is_used_in_fod: isUsedInFod,
          location: fodLocation,
        } = fodUsageDetails || {};

        if (Array.isArray(fodLocation) && isUsedInFod) {
          messages.push(...fodLocation);
        }
        if (Array.isArray(backsolveLocation) && isUsedInBacksolve) {
          messages.push(...backsolveLocation);
        }

        if (isUsedInFod || isUsedInBacksolve) {
          showDialogIfSecurityIsUsed();
        } else {
          showRegularDeleteDialog(security);
        }
      }
    },
    [visibleColumns, showDialog, deleteColumn, captableSheet]
  );

  const saveProformaColumns = useCallback(
    activeColumns => {
      if (isDisabled) {
        setIsDisabled(false);
      }

      saveAsNewProforma({
        ...updatedTableData,
        name: formState.values.name ?? `${captableInfo?.name} Proforma`,
        securities: activeColumns,
      });
    },
    [captableInfo, formState, isDisabled, saveAsNewProforma, updatedTableData]
  );

  const saveCapTable = useCallback(
    async (updateGlobalStore, saveAsProForma = false) => {
      // Save files in widget
      if (!isEmpty(filesToSave)) {
        addReferenceForExistingDocuments(
          Number(selectedMeasurementDate.id),
          filesToSave,
          Number(captableInfo?.id),
          CAP_TABLE_REFERENCE_TYPE
        );
      }

      if (!isEmpty(notes) && notesHasChanged) {
        saveNotes();
      }

      if (!isEmpty(spreadsheetNotes) && spreadsheetNotesHasChanged) {
        saveSpreadsheetNotes()
      }

      // Save table
      if (areCellsValid()) {
        const deletedColumnsIds = deletedColumns.map(col => col.id);
        const originalLedgerColumns = columnsWithLedgers.map(column => ({
          ...column,
          is_deleted: true,
          // Undefined is used to force the column to be deleted in the database
          options_ledger_data_updated: isNull(column.id) || undefined,
        }));

        const updatedFilteredColumns
          = updatedColumns?.filter(
            updatedCol => !originalLedgerColumns.map(uc => uc.security_ref).includes(updatedCol.security_ref)
          ) || [];

        // This excludes columns that were added and deleted before saving them in the database
        const persistentDeletedFilteredColumns = persistentDeletedColumns.filter(
          col => col.id !== 0 && !deletedColumnsIds.includes(col.id)
        );

        const persistentDeletedIds = persistentDeletedFilteredColumns.map(({ id }) => id);

        const hiddenNotesColumns = updatedHiddenNoteColumns.map(col => {
          const is_deleted = persistentDeletedIds.includes(col.id) ? true : col.is_deleted;
          return {
            ...col,
            is_deleted,
          };
        });

        const deletedBySecurityChangeIds = deletedBySecurityTypeChange.map(({ id }) => id);

        let activeColumns = [
          ...updatedFilteredColumns,
          ...updatedHiddenColumns.filter(col => !deletedBySecurityChangeIds.includes(col.id)),
          ...hiddenNotesColumns,
          ...hiddenColumns, // these don't have a security_ref
          ...fromNotesToSecurities,
          ...fromVirtualToReal,
        ];

        activeColumns = calcCurrentOwnPercent(activeColumns);

        if (saveAsProForma) {
          saveProformaColumns(activeColumns);
          return;
        }
        const shouldRemoveDivRateLedgerItems = (column: CaptableColumn) =>
          Boolean(
            !updatedTableData.has_dividend_rate_ledger || column.has_multiple_investments || !column.has_dividends
          );
        const shouldRemoveLiqPrefLedgerConfig = (column: CaptableColumn) =>
          Boolean(!updatedTableData.has_liquidation_preference_ledger || column.has_multiple_investments);
        const allColumns = [
          ...activeColumns,
          ...deletedColumns,
          ...deletedBySecurityTypeChange,
          ...persistentDeletedFilteredColumns,
          ...originalLedgerColumns,
        ].map(column => ({
          ...column,
          ...(shouldRemoveLiqPrefLedgerConfig(column) && {
            liquidation_preference_ledger_config: null,
          }),
          ...(shouldRemoveDivRateLedgerItems(column) && { dividend_rate_ledger_items: [] }),
          ...clearUnderlyingSecurity(column),
        })) as CaptableColumn[];

        if (isAllocationFinal) {
          saveConfirmation(async () => {
            await updateCapTableData(Number(captableInfo?.id), allColumns, updatedTableData, updateGlobalStore);
            onEditCaptable();
          });
        } else {
          await updateCapTableData(Number(captableInfo?.id), allColumns, updatedTableData, updateGlobalStore);
          onEditCaptable();
        }
        cleanAction();
      }

      let tmpValidatedCells = {};

      Object.values(cells.captable).forEach(cell => {
        // Update cells to show error feedback
        tmpValidatedCells = { ...tmpValidatedCells, [cell.key!]: cell };

        if (!cell.isValid && cell.parent) {
          setRowGroups({ type: SYSTEM_EXPAND, row: cell.parent });
        }
      });
    },
    [
      addReferenceForExistingDocuments,
      saveNotes,
      notes,
      notesHasChanged,
      areCellsValid,
      onEditCaptable,
      captableInfo,
      cells,
      cleanAction,
      columnsWithLedgers,
      deletedBySecurityTypeChange,
      deletedColumns,
      filesToSave,
      fromNotesToSecurities,
      fromVirtualToReal,
      hiddenColumns,
      isAllocationFinal,
      persistentDeletedColumns,
      saveConfirmation,
      saveProformaColumns,
      selectedMeasurementDate,
      spreadsheetNotes,
      saveSpreadsheetNotes,
      spreadsheetNotesHasChanged,
      updateCapTableData,
      updatedColumns,
      updatedHiddenColumns,
      updatedHiddenNoteColumns,
      updatedTableData,
    ]
  );

  const saveProforma = useCallback(() => {
    saveCapTable(false, true);
  }, [saveCapTable]);

  // Set page actions
  useEffect(() => {
    if (!isEmpty(captableInfo)) {
      updatePageActions?.({
        mainAction: {
          id: 'captable',
          label: 'Save',
          isActive: isDisabled,
          callback: saveCapTable,
          isLock: isAllocationFinal,
        },
        secondaryActions: [
          {
            label: 'Save as New Proforma',
            isActive: captableInfo.is_primary,
            callback: savesAsProformaConfirmation,
          },
        ],
      });
    }

    return () => {
      updatePageActions?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    captableInfo,
    cells,
    isDisabled,
    filesToSave,
    spreadsheetNotes,
    spreadsheetNotesHasChanged,
    companyInfo,
    updatedColumns,
    notes,
    updatePageActions,
    updatedTableData,
  ]);

  const isGettingInitialData = useMemo(
    () => [captableId, isAllocationFinal, isDisabled].includes(undefined),
    [captableId, isAllocationFinal, isDisabled]
  );

  useKeyEventForFabButton(isGettingInitialData);

  useEffect(
    () => () => {
      setIsAllocationFinal(undefined);
      setIsDisabled(false);
    },
    [captableId]
  );

  // Set the table disabled if the allocation is marked as final
  useEffect(() => {
    if (captableInfo?.id) {
      setCaptableId(Number(captableInfo?.id));
      const verifyAllocationStatus = async () => {
        try {
          const response = (await captableAction.verifyIfHasFinalAllocation(
            Number(captableInfo?.id)
          )) as unknown as FinalAllocation[];
          if (response) {
            setFinalAllocations(response);
          }
          setIsAllocationFinal(!isEmpty(response));
          setIsDisabled(!isEmpty(response) || viewOnlyUser);
          setDocumentDisabledStatus(viewOnlyUser);
          // any as errors are not typed
        } catch (error: any) {
          setIsAllocationFinal(false);
          setIsDisabled(false);
          throw new Error(error);
        }
      };
      verifyAllocationStatus();
    }

    return () => {
      setCaptableId(undefined);
    };
  }, [captableInfo, companyInfo, viewOnlyUser, setDocumentDisabledStatus]);

  function getErrors(
    securityType: string,
    isInvalid: boolean,
    preferredWithErrors: CapTableCell[],
    cell: CapTableCell,
    warrantWithErrors: CapTableCell[],
    optionWithErrors: CapTableCell[]
  ) {
    switch (securityType) {
      case PREFERRED_STOCK.toString():
        if (isInvalid) {
          preferredWithErrors.push(cell);
        }
        break;
      case WARRANT.toString():
        if (isInvalid) {
          warrantWithErrors.push(cell);
        }
        break;
      case OPTION.toString():
        if (isInvalid) {
          optionWithErrors.push(cell);
        }
        break;
      default:
        break;
    }
  }

  // Open row groups if find any cell with errors
  const toggleRows = useCallback(
    (isFirstRender = false) => {
      const capCells = cells.capTable as unknown as Record<string, CapTableCell>;
      const preferredWithErrors: CapTableCell[] = [];
      const warrantWithErrors: CapTableCell[] = [];
      const optionWithErrors: CapTableCell[] = [];

      each(capCells, cell => {
        if (!isFirstRender) {
          const securityTypeKey = cell.columnLegend! + SECURITY_TYPE_ROW_NUMBER;
          const securityType = toString(cells[securityTypeKey] ? cells[securityTypeKey].value : '0');
          const isInvalid = cell.isValid === false;

          getErrors(securityType, isInvalid, preferredWithErrors, cell, warrantWithErrors, optionWithErrors);
        }
      });

      if (!isFirstRender) {
        // Expand if has errors
        if (preferredWithErrors.length || warrantWithErrors.length) {
          setRowGroups({ type: SYSTEM_EXPAND, row: PREFERRED_TERMS_ALIAS });
        }
        if (optionWithErrors.length || warrantWithErrors.length) {
          setRowGroups({ type: SYSTEM_EXPAND, row: STRIKE_PRICE_ALIAS });
        }
      }
    },
    [cells]
  );

  useEffect(() => {
    if (!isEmpty(cells) && !isTableReady) {
      toggleRows(true);
      setIsTableReady(true);
    }
  }, [cells, isTableReady, toggleRows]);

  // Expand rows when PDF dialog is open
  useEffect(() => {
    if (openPdfDialog && isTableReady) {
      setRowGroups({ type: SYSTEM_EXPAND, row: PREFERRED_TERMS_ALIAS });
      setRowGroups({ type: SYSTEM_EXPAND, row: STRIKE_PRICE_ALIAS });
    }
  }, [openPdfDialog, isTableReady]);

  const getLinkedCellUpdates = useCallback(
    newCells => {
      // Get updates for linked cells
      const cellUpdates = linkedCellsUpdates({
        prevCells: cells as unknown as Record<string, CapTableCell>,
        cells: newCells,
      });

      return cellUpdates;
    },
    [cells]
  );

  const capTableContextValue = useMemo(
    () => ({
      measurementDate,
      selectedMeasurementDate,
      rowGroups,
      setRowGroups,
      isDisabled: false,
      cells,
      getLinkedCellUpdates,
      convertibleNotesFieldAttrs,
      hasVariableLiquidationPreference: hasVariableLiquidationPreferenceLedger,
      setHasVariableLiquidationPreference: setHasVariableLiquidationPreferenceLedger,
      hasVariableDividendRate,
      setHasVariableDividendRate,
      divRateLedgerItemAttrs,
    }),
    [
      cells,
      hasVariableLiquidationPreferenceLedger,
      setHasVariableLiquidationPreferenceLedger,
      hasVariableDividendRate,
      setHasVariableDividendRate,
      convertibleNotesFieldAttrs,
      getLinkedCellUpdates,
      measurementDate,
      rowGroups,
      selectedMeasurementDate,
      divRateLedgerItemAttrs,
    ]
  );

  const renderProformaCaptable = useMemo(
    () => (
      <ProformaCaptableDialog
        editMode={false}
        initialValues={initialFormValues}
        isValid={formState?.isValid || false}
        onClose={() => setOpenProformaDialog(false)}
        onFormChange={updatedFormState => setFormState(updatedFormState)}
        onSave={saveProforma}
        open={openProformaDialog || false}
        tableTerms={tableTerms}
        title={`New Proforma ${tableTerms.tableName}`}
      />
    ),
    [formState, initialFormValues, openProformaDialog, saveProforma, tableTerms]
  );

  if (!captableSheet) {
    return <GridSkeleton />;
  }

  return data && cells && captableSheet?.data ? (
    <CapTableContext.Provider value={capTableContextValue as unknown as CapTableContextType}>
      {/* Proforma Cap Table */}
      {renderProformaCaptable}

      {isDisabled && isAllocationFinal && (
        <AllocationFinalMessage
          tableTerms={tableTerms}
          finalAllocations={finalAllocations}
          setIsDisabled={setIsDisabled}
          DocumentsDisabled={setDocumentDisabledStatus}
          savesAsProformaConfirmation={savesAsProformaConfirmation}
          variant
          actions={[
            {
              label: 'Duplicate',
              buttonProps: {
                variant: 'contained',
                size: 'small',
                disabled: viewOnlyUser,
              },
              callback: savesAsProformaConfirmation,
            },
          ]}
          viewOnlyUser={viewOnlyUser}
        />
      )}

      {isAllocationFinal && !isDisabled && <Alert isAlertVisible>{CAP_TABLE_LOCKED_MESSAGE}</Alert>}

      <WorkbookContext.Provider value={workbookContextValue}>
        <div data-ref={PdfExportTarget.CAP_TABLE}>
          <ScalarSpreadsheet
            {...captableSheet}
            {...restCaptableProps}
            {...sharedProps}
            cloneColumn={cloneColumn}
            allowCloneColumn={!isDisabled}
            setColumns={setVisibleColumns}
            rowGroups={rowGroups}
            setRowGroups={setRowGroups}
            toggleRows={toggleRows}
            deleteColumn={showDeleteConfirmation}
            tableTerms={tableTerms}
            sheet={captableSheet}
            format={format}
            formatDispatch={formatDispatch}
            addMultipleColumns={!isDisabled ? addSecurity : undefined}
            allowDeleteColumn={!isDisabled}>
            {/* any due to legacy component typing */}
            {showEmptyTableMessage ? ((<EmptyTableMessage tableTerms={tableTerms} />) as any) : null}
          </ScalarSpreadsheet>
        </div>
      </WorkbookContext.Provider>
      <ExtendedFabButton
        addBottomMarginForDrawer
        id="captable-add-security-btn"
        disabled={isShowLoadingProgress || isDisabled || isButtonDisabled}
        label="Add Security"
        onClick={() => addSecurity(1)}
      />
    </CapTableContext.Provider>
  ) : (
    <></>
  );
};

export default CapTableTable;
